import type { CodeMigration } from './types.js';
import { I18N_NOOP_TEMPLATE } from '../lib/i18n-noop-template.js';

/**
 * Neutralizes the legacy parallel i18next bootstrap in a generated app's
 * `src/i18n/index.ts`.
 *
 * The pre-5.6 scaffold shipped a file that called
 * `i18next.use(LanguageDetector).use(initReactI18next).init({ resources: … })`
 * on the SAME i18next singleton the @atlashub/smartstack SDK boots. i18next's
 * init() REPLACES the resource store, so every business bundle registered
 * before it (moduleResources.generated.ts → addClientResources) is destroyed —
 * the SDK's own core namespaces are re-added asynchronously afterwards, which
 * is why ONLY business pages render raw i18n keys (e.g. `client.list.title`)
 * while the shell stays translated.
 *
 * The init template was fixed long ago (no-op `export {};`), but existing apps
 * keep the legacy file forever: `ss init --here` treats it as user-modified
 * (hash mismatch) and refuses to overwrite. This migration is the sanctioned
 * repair channel — executeMigrations writes directly (with a `.bak` backup),
 * and the content-signature detect() below is the safety guard.
 *
 * Detection is deliberately strict (ALL signals must match, every one a
 * single-line substring so CRLF files match too):
 *   - imports the `i18next` singleton (quote before the name excludes
 *     'react-i18next'),
 *   - wires `initReactI18next`,
 *   - calls `.init(`,
 *   - does NOT already use the `addClientResources` seam.
 * A customised file that uses the seam, or any exotic i18n setup, is left
 * untouched (and the `.bak` covers the rest).
 *
 * Retroactive window 1.0.0 → 6.0.0: applies to the whole 5.x fleet on every
 * `ss upgrade`; idempotent because detect() is false on the no-op output.
 */
export const i18nNeutralizeParallelInitMigration: CodeMigration = {
  fromVersion: '1.0.0',
  toVersion: '6.0.0',
  description:
    'Neutralize legacy parallel i18next init in src/i18n/index.ts (its init() clobbers the SDK resource store → business pages render raw i18n keys)',

  migrations: [
    {
      pattern: '**/web/**/src/i18n/index.ts',
      description: 'Replace the legacy i18next.init() bootstrap with the addClientResources no-op seam',

      detect: (content: string): boolean =>
        !content.includes('addClientResources') &&
        /from\s+['"]i18next['"]/.test(content) &&
        content.includes('initReactI18next') &&
        content.includes('.init('),

      transform: (): string => I18N_NOOP_TEMPLATE,
    },
  ],
};
